import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Gift, AlertCircle } from "lucide-react";
import { setPendingShopifyContext } from "@/lib/shopifyInstall";
import { buildEmbeddedAppPath, setShopifyEmbeddedContext } from "@/lib/shopifyEmbedded";

/**
 * This page handles Shopify OAuth redirects.
 * It stores the pending install context locally and redirects to merchant-login.
 * Sensitive Shopify tokens stay server-side until the authenticated user finalizes install.
 */
function ShopifyRedirect() {
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const sessionToken = searchParams.get("session") || "";
  const shop = searchParams.get("shop") || "";
  const shopEmail = searchParams.get("shop_email") || "";
  const shopName = searchParams.get("shop_name") || "";
  const shopCurrency = searchParams.get("shop_currency") || "";
  const host = searchParams.get("host") || "";
  const embedded = searchParams.get("embedded") === "1";

  useEffect(() => {
    // Prevent multiple processing
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    async function processRedirect() {
      if (!sessionToken) {
        setError("Missing session token");
        return;
      }

      try {
        setShopifyEmbeddedContext({
          embedded: embedded || Boolean(host),
          host,
          shop,
        });

        setPendingShopifyContext({
          sessionToken,
          shop,
          shopEmail,
          shopName,
          shopCurrency,
        });

        // Build the redirect URL
        const params = new URLSearchParams();
        params.set("platform", "shopify");
        params.set("tab", "signup");
        // Carry the short-lived opaque install session into the embedded
        // login page. The login page validates it as a UUID before using it,
        // which also prevents Shopify's non-UUID App Bridge session id from
        // being treated as an install session.
        params.set("session", sessionToken);
        if (shop) {
          params.set("shop", shop);
        }
        if (shopName) {
          params.set("shop_name", shopName);
        }
        if (shopEmail) {
          params.set("shop_email", shopEmail);
        }
        if (shopCurrency) {
          params.set("shop_currency", shopCurrency);
        }
        if (host) {
          params.set("host", host);
        }
        if (embedded || host) {
          params.set("embedded", "1");
        }

        const targetUrl = buildEmbeddedAppPath("/merchant-login", params);

        // Navigate to the signup page
        window.location.href = targetUrl;
      } catch (err) {
        console.error("Error processing redirect:", err);
        setError("An error occurred. Please try again.");
      }
    }

    processRedirect();
  }, [embedded, host, sessionToken, shop, shopCurrency, shopEmail, shopName]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-foreground mb-2">
            Installation Error
          </h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <a
            href="https://apps.shopify.com"
            className="text-primary hover:underline"
          >
            Return to Shopify App Store
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <Gift className="h-8 w-8 text-primary" />
          </div>
        </div>
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-foreground mb-2">
          Setting up Splitkart...
        </h1>
        <p className="text-muted-foreground">
          Connecting your Shopify store
        </p>
      </div>
    </div>
  );
}

export default ShopifyRedirect;
